import { getBrazeExternalId } from 'utils/getBrazeExternalId';
import { ReducedUserTokenState } from 'lib/utils/parseUser';

const FBEVENTS_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

type Fbq = NonNullable<Window['fbq']>;

interface MetaPixelStub {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  push: MetaPixelStub;
  loaded: boolean;
  version: string;
  queue: unknown[][];
}

const loadPixelScript = (): Fbq => {
  if (window.fbq) return window.fbq;

  const stub = function fbq(...args: unknown[]) {
    if (stub.callMethod) {
      stub.callMethod(...args);
    } else {
      stub.queue.push(args);
    }
  } as MetaPixelStub;
  stub.push = stub;
  stub.loaded = true;
  stub.version = '2.0';
  stub.queue = [];
  window.fbq = stub;
  window._fbq = stub;

  const script = document.createElement('script');
  script.async = true;
  script.src = FBEVENTS_SRC;
  const first = document.getElementsByTagName('script')[0];
  if (first?.parentNode) {
    first.parentNode.insertBefore(script, first);
  } else {
    document.head.appendChild(script);
  }

  return stub;
};

class MetaPixelService {
  private static instance: MetaPixelService;

  private pixelId?: string;

  private fbq?: Fbq;

  private identifiedExternalId?: string;

  constructor() {
    if (typeof window === 'undefined') return;

    const pixelId = process.env.NEXT_PUBLIC_META_PIXEL_ID;

    if (!pixelId) {
      if (process.env.ENVIRONMENT_NAME === 'development') {
        // eslint-disable-next-line no-console
        console.warn(
          'Meta Pixel not initialized: missing NEXT_PUBLIC_META_PIXEL_ID',
        );
      }
      return;
    }

    this.pixelId = pixelId;
  }

  public static getInstance(): MetaPixelService {
    if (!MetaPixelService.instance) {
      MetaPixelService.instance = new MetaPixelService();
    }
    return MetaPixelService.instance;
  }

  // Returns true when the identity handed to Meta changed.
  public changeUser(user: ReducedUserTokenState): boolean {
    if (!user.email || !this.pixelId) return false;

    const externalId = getBrazeExternalId(user.email);
    if (!externalId || externalId === this.identifiedExternalId) return false;

    // fbevents.js is only ever fetched once a user is identified.
    this.fbq ??= loadPixelScript();

    // autoConfig must be off before init. Tu
    this.fbq('set', 'autoConfig', false, this.pixelId);

    // fbevents.js warns about a repeated init for the same pixel ID, but init
    // is the only way to hand it advanced-matching data, so a user change
    // mid-session has to go through it.
    this.fbq('init', this.pixelId, {
      external_id: externalId,
      em: user.email,
      fn: user.first_name ?? '',
      ln: user.last_name ?? '',
    });
    this.identifiedExternalId = externalId;
    return true;
  }

  private isIdentified(): boolean {
    return Boolean(this.identifiedExternalId);
  }

  // Meta only transmits the identity from init when an event fires.
  public trackUserIdentified() {
    if (!this.isIdentified() || !this.fbq) return;

    this.fbq('trackCustom', 'UserIdentified');
  }
}

export default MetaPixelService;
